import * as CommentsActionTypes from '../../types/comments';
import * as CommentsApis from '../../apis/comments';
import * as CommentsErrors from '../../errors/comments';
import * as LoadingActions from '../loading';
import * as SettingsSelectors from '../../selectors/settings';
import * as CallbacksSelectors from '../../selectors/callbacks';
import * as CurrentsSelectors from '../../selectors/current';
import * as CollaboratorsSelectors from '../../selectors/collaborators';
import * as GroupsSelectors from '../../selectors/groups';
import { commentEventsBus, COMMENT_ADDED_EVENT } from '../../eventsBus/comment';
import reportToBigQuery from '../../../Utils/bigQueryReporter';
import * as CommentsSelectors from '../../selectors/comments';

/**
 * A comment
 * @typedef {Object} Comment
 * @property {string} id - comment id
 * @property {string} collaboratorId - id of collaborator who wrote the comment
 * @property {string} body - the comment content
 * @property {number} timecode - timecode in video/audio
 * @property {array} annotations - comments annotations
 * @property {string} parentId - for replies, what comment they belong to
 * @property {string} groupId - id of the group the collaborator belongs to
 * @property {string} fileId - file id
 * @property {number} createdAt - when the comment was created
 * @property {string} reviewId - review the comments belongs to
 */

/**
 * Sets all the comments from the server
 * @param {Object} payload {<commentId>: <commentData>}
 */
export const setComments = (payload) => ({
    type: CommentsActionTypes.SET_COMMENTS,
    payload
});

/**
 * Update a comment
 * @param {Object} payload {<commentId>: <id>, <field>: <newValue>....}
 */
export const updateComment = (payload) => ({
    type: CommentsActionTypes.UPDATE_COMMENT,
    payload
});

/**
 * Delete a comment
 * @param {String} id comment id
 */
export const deleteComment = (id) => ({
    type: CommentsActionTypes.DELETE_COMMENT,
    payload: id
});

/**
 * Adds a new comment
 * @param {Comment} payload - the comment to add
 */
export const addComment = (payload) => {
    if (payload && payload.id) {
        commentEventsBus.emit(COMMENT_ADDED_EVENT, payload.id);
    }

    return ({
        type: CommentsActionTypes.ADD_COMMENT,
        payload
    });
};

/**
 *
 * @param {object} payload
 * @param {array<Comment>} payload.comments - comments to create
 * @param {string} payload.reviewType - type of the comments bulk to create
 * @param {string} [payload.reviewMessage] - global message linked to the comments bulk to create
 */
export const createComments = (payload) => (dispatch, getState) =>
    Promise.resolve().then(() => {
        dispatch(LoadingActions.setCreateCommentsLoading(true));
    }).then(() => ({
        ...payload,
        contextType: SettingsSelectors.getContextType(getState()),
        contextId: SettingsSelectors.getContextId(getState())
    })).then((payloadEnriched) => {
        if (Array.isArray(payload.comments) && payload.comments.length === 0) {
            return [];
        }

        return CommentsApis.createComments(payloadEnriched);
    }).then((comments) => {
        const pendingComments = CommentsSelectors.getPendingComments(getState());
        for (const comment of pendingComments){
            dispatch(updateComment({ ...comment, pending: false }));
        }
        payload.comments.forEach(({ id }) =>
            dispatch(deleteComment(id))
        );

        comments.forEach((comment) =>
            dispatch(addComment(comment))
        );

        return comments;
    }).then((comments) => {
        const reviewMessage = payload.reviewMessage;
        const commentIds = comments.map(({ id }) => id);
        const reviewId = comments[0] ? comments[0].reviewId : undefined;
        const groupType = GroupsSelectors.getCurrentGroupType(getState());

        return CallbacksSelectors.onReviewCreated(
            getState(),
            commentIds,
            reviewId,
            reviewMessage,
            groupType
        ).then(() => commentIds);
    }).then((commentIds) => {
        reportToBigQuery({
            group: 'conversation',
            type: 'message.sent',
            order: { id: SettingsSelectors.getContextId(getState()) },
            conversation: {
                message: {
                    sender_id: CurrentsSelectors.getCurrentUserId(getState()),
                    recipient_id: CollaboratorsSelectors.getNotCurrentCollaboratorUserIdInSellerGroup(getState()),
                    body: payload.reviewMessage,
                    format: 'rejection'
                },
                type: 'order'
            }
        });

        dispatch(LoadingActions.setCreateCommentsLoading(false));

        return commentIds;
    }).catch((error) => {
        dispatch(LoadingActions.setCreateCommentsLoading(false));

        throw error;
    }).catch((error) => {
        if (!error.code) {
            error.code = CommentsErrors.COMMENTS_ACTIONS_CREATE_COMMENTS_UNEXPECTED;
            error.details = { payload: JSON.stringify(payload) };
        }

        throw error;
    });
